import { defineStore } from "pinia";
import { fetchApi } from "@/config/api";

let checkAuthPromise: Promise<boolean> | null = null;
let sessionEpoch = 0;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null as any,
    token: null as string | null,
    authenticated: false,
    isLoading: false,
    isInitialized: false,
    /** Impersonation admin (cookie HttpOnly) — pas de token en JS. */
    isImpersonating: false,
    impersonationDisplayName: "" as string,
  }),

  getters: {
    isAuthenticated: (state) => state.authenticated,
  },

  actions: {
    /** Défini par la mutation impersonate ou la query impersonationStatus (cookie). */
    setImpersonationFromCookie(active: boolean, adminDisplayName?: string) {
      this.isImpersonating = active;
      this.impersonationDisplayName = adminDisplayName ?? "";
    },
    clearImpersonation() {
      this.isImpersonating = false;
      this.impersonationDisplayName = "";
    },
    /** Réinitialise l'état vendeur (impersonation ou changement de compte). */
    resetForImpersonation() {
      this.user = null;
      this.authenticated = false;
      this.isInitialized = false;
      checkAuthPromise = null;
      localStorage.removeItem("user_mel");
      localStorage.removeItem("vendeur_info_header");
    },
    setUser(userData: any) {
      sessionEpoch += 1;
      const id_vendeur = userData.id_vendeur ?? userData.id;
      this.user = {
        id: id_vendeur, // Mapper id_vendeur vers id
        id_vendeur: id_vendeur,
        ...userData,
      };
      this.authenticated = true;

      // Stocker mel dans localStorage pour disponibilité immédiate
      if (userData.mel !== undefined && userData.mel !== null) {
        localStorage.setItem("user_mel", String(userData.mel));
      }
    },

    // Récupérer mel depuis localStorage
    getMelFromStorage(): string | null {
      return localStorage.getItem("user_mel");
    },

    async checkAuth(options?: { force?: boolean }): Promise<boolean> {
      if (this.isInitialized && !options?.force) {
        return this.authenticated;
      }

      if (checkAuthPromise && !options?.force) {
        return checkAuthPromise;
      }

      if (options?.force) {
        checkAuthPromise = null;
        this.isInitialized = false;
      }

      this.isLoading = true;

      checkAuthPromise = (async () => {
        try {
          const query = `query { 
            me { id_vendeur email nomBoutique siret tva mel jetons } 
          }`;
          const data = await fetchApi<{ me: any }>(query, undefined, {
            skipAuthRedirect: true,
          });

          if (data?.me) {
            this.setUser(data.me);
            this.authenticated = true;
          } else {
            this.user = null;
            this.authenticated = false;
          }

          return this.authenticated;
        } catch (error) {
          // Ne pas bloquer l'application si l'erreur est une erreur réseau ou 400
          // L'utilisateur pourrait être connecté mais l'API temporairement indisponible
          const errorMessage =
            error instanceof Error ? error.message : String(error);

          // Si l'erreur est "Non authentifié" ou "Unauthorized", déconnecter
          if (
            errorMessage.includes("Non authentifié") ||
            errorMessage.includes("Unauthorized") ||
            errorMessage.includes("Authentification requise")
          ) {
            this.user = null;
            this.authenticated = false;
            return false;
          }

          // Pour les autres erreurs (400, réseau, etc.), ne pas déconnecter si l'utilisateur semble connecté
          const storedMel = localStorage.getItem("user_mel");
          if (storedMel && this.user) {
            // Garder l'état actuel si l'utilisateur semble déjà connecté
            // Ne pas logger en mode production pour éviter le bruit
            if (process.env.NODE_ENV === "development") {
              // Erreur API - état d'authentification conservé
            }
            return this.authenticated;
          }

          // Si pas de données en localStorage, considérer comme non authentifié
          this.user = null;
          this.authenticated = false;
          return false;
        } finally {
          this.isLoading = false;
          this.isInitialized = true;
          checkAuthPromise = null;
        }
      })();

      return checkAuthPromise;
    },

    async logout() {
      const logoutEpoch = sessionEpoch;
      const wasImpersonating = this.isImpersonating;
      try {
        if (wasImpersonating) {
          const { adminEfpClearImpersonation } = await import("@/api/adminEfp");
          await adminEfpClearImpersonation();
        } else {
          const mutation = `mutation { logout { message } }`;
          await fetchApi(mutation, undefined, { skipAuthRedirect: true });
        }
      } catch {
        /* ignorer les erreurs */
      }

      if (logoutEpoch !== sessionEpoch) return;

      this.clearImpersonation();
      this.user = null;
      this.authenticated = false;
      this.token = null;
      localStorage.removeItem("user_mel");

      const path = window.location.pathname.replace(/\/$/, "") || "/";
      const isPublicPage =
        path === "/auth" ||
        path === "/register" ||
        path === "/reset-password" ||
        path.endsWith("/auth") ||
        path.endsWith("/register") ||
        path.endsWith("/reset-password") ||
        path.startsWith("/adminEFP");
      if (isPublicPage) return;
      window.location.href = wasImpersonating ? "/adminEFP" : "/auth";
    },
  },
});
